import { Shield, Target, Users, Zap, AlertTriangle } from 'lucide-react';
import RiskBadge from './RiskBadge';
import { cn } from '@/lib/utils';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface ThreatActor {
  name: string;
  motivation: string;
  capability: RiskLevel;
  likelihood: RiskLevel;
}

interface AttackVector {
  name: string;
  description: string;
  riskLevel: RiskLevel;
  mitigations: string[];
}

interface ThreatModelingProps {
  threatActors: ThreatActor[];
  attackVectors: AttackVector[];
  assetsCritical: string[];
}

const ThreatModeling = ({ threatActors, attackVectors, assetsCritical }: ThreatModelingProps) => {
  return (
    <div className="space-y-6">
      {/* Critical Assets */}
      <div className="card-elevated p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gradient-to-r from-primary to-primary/70 p-2 rounded-lg">
            <Shield className="w-5 h-5 text-primary-foreground" />
          </div>
          <h3 className="text-lg font-semibold">Critical Assets at Risk</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {(assetsCritical || []).map((asset, index) => (
            <div
              key={index}
              className="bg-risk-high-bg text-risk-high px-3 py-2 rounded-lg text-sm font-medium border border-risk-high/20"
            >
              {asset}
            </div>
          ))}
        </div>
      </div>

      {/* Threat Actors */}
      <div className="card-elevated p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gradient-to-r from-risk-critical to-risk-high p-2 rounded-lg">
            <Users className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold">Potential Threat Actors</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Actor Type</th>
                <th>Motivation</th>
                <th>Capability</th>
                <th>Likelihood</th>
              </tr>
            </thead>
            <tbody>
              {(threatActors || []).map((actor, index) => (
                <tr key={index}>
                  <td className="font-medium">{actor.name}</td>
                  <td className="text-muted-foreground">{actor.motivation}</td>
                  <td>
                    <RiskBadge level={actor.capability} />
                  </td>
                  <td>
                    <RiskBadge level={actor.likelihood} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attack Vectors */}
      <div className="card-elevated p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gradient-to-r from-risk-medium to-risk-high p-2 rounded-lg">
            <Target className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold">Attack Vectors Analysis</h3>
        </div>
        <div className="space-y-4">
          {(attackVectors || []).map((vector, index) => (
            <div
              key={index}
              className={cn(
                'border rounded-lg p-4 transition-all hover:shadow-md',
                vector.riskLevel === 'critical' && 'border-l-4 border-l-risk-critical',
                vector.riskLevel === 'high' && 'border-l-4 border-l-risk-high',
                vector.riskLevel === 'medium' && 'border-l-4 border-l-risk-medium',
                vector.riskLevel === 'low' && 'border-l-4 border-l-risk-low'
              )}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold">{vector.name}</span>
                </div>
                <RiskBadge level={vector.riskLevel} />
              </div>
              <p className="text-sm text-muted-foreground mb-3">{vector.description}</p>
              <div className="bg-muted/50 rounded-lg p-3">
                <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Mitigations
                </h5>
                <ul className="space-y-1">
                  {vector.mitigations.map((mitigation, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <AlertTriangle className="w-3 h-3 text-risk-medium mt-1 flex-shrink-0" />
                      {mitigation}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThreatModeling;
